import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

/**
 * AVISO: o par do `useConfirm` para o que não pergunta, só informa.
 *
 * Substitui o `alert()` nativo, que trava a página, quebra a automação do
 * navegador e não tem tom (erro e sucesso saem iguais). Mesmo visual do toast que
 * o App já usava, agora disponível para qualquer tela, sem prop drilling.
 *
 *   const aviso = useAviso();
 *   aviso('Erro ao salvar empresa do grupo', 'error');
 *
 * Erro fica 7s na tela (é o que a pessoa precisa ler); o resto, 4s. Clique fecha.
 */
export type TomDoAviso = 'success' | 'error' | 'warning' | 'info';

interface ItemDeAviso {
  id: number;
  mensagem: string;
  tom: TomDoAviso;
}

type Avisar = (mensagem: string, tom?: TomDoAviso) => void;

const AvisoContext = createContext<Avisar | null>(null);

const COR: Record<TomDoAviso, string> = {
  success: 'bg-green-500 border-green-600',
  error: 'bg-red-500 border-red-600',
  warning: 'bg-yellow-500 border-yellow-600',
  info: 'bg-blue-500 border-blue-600',
};
const ICONE: Record<TomDoAviso, string> = { success: '✓', error: '✕', warning: '⚠', info: 'ℹ' };

export const useAviso = (): Avisar => {
  const ctx = useContext(AvisoContext);
  // Sem o provider (teste isolado de componente), não quebra a tela.
  return ctx || ((m: string) => { console.warn('[aviso]', m); });
};

export const AvisoProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [itens, setItens] = useState<ItemDeAviso[]>([]);
  const seq = useRef(0);

  const fechar = useCallback((id: number) => setItens((xs) => xs.filter((x) => x.id !== id)), []);

  const avisar = useCallback<Avisar>((mensagem, tom = 'info') => {
    const id = ++seq.current;
    setItens((xs) => [...xs.slice(-3), { id, mensagem, tom }]);
    setTimeout(() => fechar(id), tom === 'error' ? 7000 : 4000);
  }, [fechar]);

  const value = useMemo(() => avisar, [avisar]);

  return (
    <AvisoContext.Provider value={value}>
      {children}
      {itens.length > 0 && (
        <div className='fixed bottom-4 right-4 z-[110] flex flex-col gap-2 max-w-md' role='status' aria-live='polite'>
          {itens.map((a) => (
            <button
              key={a.id}
              type='button'
              onClick={() => fechar(a.id)}
              className={`flex items-start gap-3 px-4 py-3 rounded-lg shadow-xl border text-white text-sm text-left ${COR[a.tom]}`}
              title='Fechar'
            >
              <span className='font-bold'>{ICONE[a.tom]}</span>
              <span className='whitespace-pre-line'>{a.mensagem}</span>
            </button>
          ))}
        </div>
      )}
    </AvisoContext.Provider>
  );
};
